import { z } from 'zod';

import { centsSchema } from '@nesso/common-contracts/cents';
import { metadata } from '@nesso/common-contracts/schema-metadata';

// The ticket's service code list (`docs/specs/NSS-597-services-settings/service_codes.csv`), in
// file order, which is ascending by code. Changing it is a published-language change.
export const SERVICE_CODES = [
    { code: '00000', description: 'Consultation (No charge)' },
    { code: '90791', description: 'Psychiatric Diagnostic Evaluation' },
    { code: '90832', description: 'Psychotherapy, 30 minutes' },
    { code: '90834', description: 'Psychotherapy, 45 minutes' },
    { code: '90837', description: 'Psychotherapy, 60 minutes' },
    { code: '90839', description: 'Psychotherapy for crisis initial 60 minutes' },
    { code: '90846', description: 'Family psychotherapy without patient present' },
    { code: '90847', description: 'Family psychotherapy with patient present' },
    { code: '90849', description: 'Multiple-family group psychotherapy' },
    { code: '90853', description: 'Group psychotherapy' },
    { code: '90867', description: 'Therapeutic Repetitive Transcranial Magnetic Stimulation' },
    { code: '90868', description: 'Subsequent TMS Delivery and Management' },
    { code: '90870', description: 'Electroconvulsive Therapy' },
    { code: '90880', description: 'Hypnotherapy' },
    { code: '96127', description: 'Brief emotional/behavioral assessment' },
    {
        code: '96130',
        description: 'Psychological testing, evaluation by a psychologist, initial 60 minutes',
    },
    {
        code: '96136',
        description: 'Psychological test administration and scoring, initial 30m',
    },
    {
        code: '96138',
        description: 'Psychological or neuropsychological test, by technician, first 30 minutes',
    },
    {
        code: '96158',
        description: 'Health behavior intervention, individual, initial 30 minutes',
    },
    {
        code: '96164',
        description: 'Health behavior intervention, group (2 or more patients), initial 30 minutes',
    },
] as const;

// zod v4's `.extend()` drops object-level `.meta()`, so metadata is applied per field rather
// than on the object (contracts.md).
export const serviceSchema = z.object({
    code: z.string().trim().min(1).meta(metadata.nonSensitive),
    description: z.string().trim().min(1).meta(metadata.nonSensitive),
    rateCents: centsSchema.meta(metadata.nonSensitive),
    durationMinutes: z.number().int().positive().meta(metadata.nonSensitive),
    isDefault: z.boolean().meta(metadata.nonSensitive),
});

export type Service = z.infer<typeof serviceSchema>;

export const customServiceCodeSchema = z.object({
    code: z.string().trim().min(1).meta(metadata.nonSensitive),
    description: z.string().trim().min(1).meta(metadata.nonSensitive),
});

export type CustomServiceCode = z.infer<typeof customServiceCodeSchema>;

export const serviceValuesSchema = serviceSchema.omit({ isDefault: true });

export type ServiceValues = z.infer<typeof serviceValuesSchema>;
